import { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import ModalShell from './ModalShell';
import {
    approveAttendancePunch,
    rejectAttendancePunch,
} from '../../../../redux/actions/SocietyPortal/AttendanceAction/AttendanceAction';
import { formatIstCustom } from '../../../../utils/helpers/istDate';

const formatDateTime = (dateStr) => {
    if (!dateStr) return '—';
    return formatIstCustom(dateStr, {
        day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
    });
};

// Approve / reject a flagged punch — PATCH /api/attendance/:id/{approve,reject}
// (attendance.approve). Approving only clears the review flag (time was
// already counted); rejecting removes the punch's time, so notes are required.
const PunchDecisionModal = ({ punch, mode, onClose, onDone }) => {
    const dispatch = useDispatch();
    const { punchDecisionLoading } = useSelector(state => state.AttendanceReducer || {});
    const [notes, setNotes] = useState('');

    useEffect(() => { setNotes(''); }, [punch, mode]);

    if (!punch || !mode) return null;
    const isReject = mode === 'reject';

    const handleSubmit = () => {
        if (isReject && notes.trim().length === 0) {
            return toast.warn('Notes are required when rejecting — say why the time is being removed.');
        }
        const action = isReject ? rejectAttendancePunch : approveAttendancePunch;
        dispatch(action({
            punch_id: punch.punch_id,
            notes: notes.trim() || undefined,
            onSuccess: (result) => {
                onClose();
                onDone?.(result);
            },
            onError: (info) => {
                // 409 PUNCH_ALREADY_DECIDED: someone else got there first — the
                // queue entry is stale, refresh it.
                if (info.code === 'PUNCH_ALREADY_DECIDED') {
                    onClose();
                    onDone?.(info.data || null);
                }
            },
        }));
    };

    return (
        <ModalShell
            isOpen
            title={isReject ? 'Reject punch' : 'Approve punch'}
            icon={isReject ? 'mdi:close-octagon-outline' : 'mdi:check-decagram-outline'}
            onClose={onClose}
            busy={punchDecisionLoading}
            actions={
                <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={punchDecisionLoading}
                    className={`tw-flex tw-items-center tw-gap-1.5 tw-px-4 tw-py-1.5 tw-rounded-md tw-text-white tw-text-xs tw-font-medium disabled:tw-opacity-50 ${isReject ? 'tw-bg-red-600 hover:tw-bg-red-700' : 'tw-bg-emerald-600 hover:tw-bg-emerald-700'}`}
                >
                    {punchDecisionLoading ? <Icon icon="mdi:loading" className="tw-animate-spin" /> : <Icon icon={isReject ? 'mdi:close' : 'mdi:check'} />}
                    {isReject ? 'Reject' : 'Approve'}
                </button>
            }
        >
            <div className="tw-flex tw-flex-col tw-gap-3">
                <div className="tw-bg-gray-50 tw-rounded-md tw-p-3 tw-text-xs tw-text-gray-600">
                    <div className="tw-font-medium tw-text-gray-800">
                        {punch.employee?.name || `Employee #${punch.employee?.employee_id ?? ''}`}
                        {punch.employee?.employee_code ? ` (${punch.employee.employee_code})` : ''}
                    </div>
                    <div className="tw-mt-0.5">
                        {punch.punch_type} · {formatDateTime(punch.punch_at)}
                        {punch.distance_meters != null ? ` · ${Math.round(punch.distance_meters)}m from premises` : ''}
                    </div>
                    {punch.location_name && <div className="tw-text-gray-400">{punch.location_name}</div>}
                    {/* The device the punch came from - often what settles a doubtful punch. */}
                    {(punch.device_summary || punch.device_type || punch.ip_address) && (
                        <div className="tw-flex tw-items-center tw-gap-1 tw-mt-1 tw-text-gray-500">
                            <Icon icon={{ mobile: 'mdi:cellphone', tablet: 'mdi:tablet', web: 'mdi:monitor' }[punch.device_type] || 'mdi:devices'} className="tw-flex-shrink-0" />
                            <span>
                                {[
                                    punch.device_summary || punch.device_type,
                                    punch.device_id && `ID ${punch.device_id}`,
                                    punch.ip_address && `IP ${punch.ip_address}`,
                                ].filter(Boolean).join(' · ')}
                            </span>
                        </div>
                    )}
                </div>

                <p className={`tw-text-[11px] tw-rounded-md tw-p-2 tw-m-0 tw-flex tw-items-start tw-gap-1.5 ${isReject ? 'tw-text-red-700 tw-bg-red-50' : 'tw-text-emerald-700 tw-bg-emerald-50'}`}>
                    <Icon icon="mdi:information-outline" className="tw-flex-shrink-0 tw-mt-0.5" />
                    {isReject
                        ? "Rejecting removes this punch's time from the day's worked hours."
                        : 'Approving confirms the punch — the time was already counted, totals stay the same.'}
                </p>

                <div>
                    <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-500 tw-mb-1">
                        Notes {isReject ? <span className="tw-text-red-500">*</span> : <span className="tw-text-gray-400">(optional)</span>}
                    </label>
                    <textarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        rows={2}
                        maxLength={1000}
                        placeholder={isReject ? 'Why is the time being removed?' : 'e.g. Verified with site supervisor'}
                        className="tw-w-full tw-border tw-border-gray-200 tw-rounded-md tw-px-3 tw-py-2 tw-text-sm focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500"
                    />
                </div>
            </div>
        </ModalShell>
    );
};

export default PunchDecisionModal;
